import { fn, get } from '@ember/helper';
import { action, set } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismCard,
  PrismIcon,
  PrismInput,
  PrismPerfectScrollbar,
} from '@mylighthouse/prism-ember';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import { CURRENCY_MAPPING } from 'frontend-utils/utils/constants/currency-mapping-constants';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import GeneralEmptyStateOtbBanner from 'lumen/components/general/empty-state-otb-banner';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import HotelOverviewDashboardSettingsAlertDaysIndicator from 'lumen/components/hotel/overview-dashboard/settings/alert-days-indicator';
import HotelOverviewDashboardSettingsAlertSettingOptInCheckbox from 'lumen/components/hotel/overview-dashboard/settings/alert-setting-opt-in-checkbox';
import isOdTileVisible from 'lumen/helpers/is-od-tile-visible';
import { RATE_PARAMS } from 'lumen/utils/constants/general-constants';
import {
  OTB_SS_ENTRY_POINTS,
  OTB_SS_VIA_QP_VALUES,
} from 'lumen/utils/constants/otb-selfservice-constants';
import {
  DATA_FETCH_ERRORS,
  OD_ALERTS_KEYS,
  OD_REF_DAYS_AGO_VALUES,
  OD_SELECTED_TILES_IDS,
  OD_SETTINGS_KEYS,
} from 'lumen/utils/constants/overview-dashboard-constants';
import { mapRateParamToAPIParams } from 'lumen/utils/rates-request';
import { timeout } from 'lumen/utils/waiters';

interface HotelOverviewDashboardModalsAlertsSettingsSignature {
  Args: {
    closeModal: unknown;
  };
}

export default class HotelOverviewDashboardModalsAlertsSettings extends Component<HotelOverviewDashboardModalsAlertsSettingsSignature> {
  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare myotbSs: Services['myotb-ss'];
  @service declare odAccess: Services['od-access'];
  @service declare odSelections: Services['od-selections'];
  @service declare toast: Services['toast'];

  @tracked alertsDataFetchErrors = [];
  @tracked alertsDayData = null;
  @tracked alertsOptInState = null;
  @tracked thresholdValues = null;

  overviewSettingsOnLoad = null;

  alertsDataRequestTask = task(
    { restartable: true },
    async (
      otbPickupThreshold,
      marketOtbPickupThreshold,
      adrPickupThreshold,
      compsetChangeThreshold,
      otbShortTermRental,
      adrShortTermRental,
    ) => {
      let apiResponse = null;

      try {
        // We don't pass ota/rateparam/compset, so that API will use default (we want that for the settings modal numbers)
        const standardParams = {
          from_date_range_start: this.hotelAndMarketService
            .getLocalTime()
            .format('YYYY-MM-DD'),
          from_date_range_end: this.hotelAndMarketService
            .getLocalTime()
            .add(30, 'day')
            .format('YYYY-MM-DD'),
          // @ts-expect-error: Incorrect type
          subscription_id: this.hotelAndMarketService.hotel.id,
          compset_ids: 1,
          ota: OTA_IDS.BOOKINGDOTCOM,
          los: this.defaultLosRates,
          persons: this.defaultPersons,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          compare_extract_date:
            // @ts-expect-error: Incorrect type
            this.odSelections.refDaysAgoDatesInAPIFormat[
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              OD_REF_DAYS_AGO_VALUES.yesterday
            ],
          // Note: always pass the opt_in keys with value true for now (we don't look at the opt-in checkbox value for the alert days indicators)
          overview_market_otb_pickup_opt_in: true,
          overview_otb_pickup_opt_in: true,
          overview_price_position_change_opt_in: true,
          overview_price_opportunity_opt_in: true,
          overview_adr_pickup_opt_in: true,
          overview_compset_change_opt_in: true,
        };

        mapRateParamToAPIParams(RATE_PARAMS.BEST_FLEX, standardParams);

        // Threshold settings overrides (so we can get number of alerts for the values entered in the settings modal)
        if (otbPickupThreshold !== null) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          standardParams.overview_otb_pickup_threshold = otbPickupThreshold;
        }
        if (marketOtbPickupThreshold !== null) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          standardParams.overview_market_otb_pickup_threshold =
            marketOtbPickupThreshold;
        }
        if (adrPickupThreshold !== null) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          standardParams.overview_adr_pickup_threshold = adrPickupThreshold;
        }
        if (compsetChangeThreshold !== null) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          standardParams.overview_compset_change_threshold =
            compsetChangeThreshold;
        }
        if (otbShortTermRental !== null) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          standardParams.overview_short_term_rental_otb_pickup_threshold =
            otbShortTermRental;
        }
        if (adrShortTermRental !== null) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          standardParams.overview_short_term_rental_rate_pickup_threshold =
            adrShortTermRental;
        }

        const requestUrl = formatUrl(
          '/api/v3/overview/alerts/',
          // @ts-expect-error: Incorrect type
          standardParams,
          this.ajax.host,
        );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        apiResponse = await this.ajax.request(requestUrl);
      } catch (error) {
        // Basic error logging for now. Just that we are aware if something goes wrong
        this.errorTracking.captureException(error);
      }

      if (apiResponse) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return apiResponse;
      }

      return null; // in this case something went wrong with the request
    },
  );
  requestAndSetAlertsDataTask = task(
    { restartable: true },
    async (
      otbPickupThreshold = null,
      marketOtbPickupThreshold = null,
      adrPickupThreshold = null,
      compsetChangeThreshold = null,
      otbShortTermRental = null,
      adrShortTermRental = null,
    ) => {
      await timeout();

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const response = await this.alertsDataRequestTask.perform(
        otbPickupThreshold,
        marketOtbPickupThreshold,
        adrPickupThreshold,
        compsetChangeThreshold,
        otbShortTermRental,
        adrShortTermRental,
      );

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      this.alertsDayData = response.alerts;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      this.alertsDataFetchErrors = response.meta.data_fetch_errors;
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ADRCurrencySymbol() {
    return CURRENCY_MAPPING[
      // @ts-expect-error: Incorrect type
      this.hotelAndMarketService.hotel.revenueInsightCurrency
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get alertsDayIndicatorsData() {
    let result = {
      [OD_ALERTS_KEYS.otb_pickup]: null,
      [OD_ALERTS_KEYS.market_otb_pickup]: null,
      [OD_ALERTS_KEYS.adr_pickup]: null,
      [OD_ALERTS_KEYS.compset_change]: null,
      [OD_ALERTS_KEYS.short_term_rental_otb_pickup]: null,
      [OD_ALERTS_KEYS.short_term_rental_rate_pickup]: null,
    };

    if (!this.isAlertsDataRequestLoading && this.alertsDayData) {
      // @ts-expect-error: Incorrect type
      const allAlertsInNextThirtyDays = [];

      // get all alerts in the next 30 days and push them in 1 flat array
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.alertsDayData.forEach((fromDateDate) => {
        allAlertsInNextThirtyDays.push(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
          ...fromDateDate.alerts.map((alertData) => alertData.name),
        );
      });

      // @ts-expect-error: Incorrect type
      result = {
        [OD_ALERTS_KEYS.otb_pickup]: 0,
        [OD_ALERTS_KEYS.market_otb_pickup]: 0,
        [OD_ALERTS_KEYS.adr_pickup]: 0,
        [OD_ALERTS_KEYS.compset_change]: 0,
        [OD_ALERTS_KEYS.short_term_rental_otb_pickup]: 0,
        [OD_ALERTS_KEYS.short_term_rental_rate_pickup]: 0,
      };

      // Loop over all alerts and count how much they occur
      // @ts-expect-error: Incorrect type
      allAlertsInNextThirtyDays.forEach((alert) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (result[alert] !== undefined) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          result[alert] += 1;
        }
      });
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get defaultLosRates() {
    return (this.hotelAndMarketService.hotelOrMarket?.defaultLosRates ?? [])[0];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get defaultPersons() {
    return this.hotelAndMarketService.hotelOrMarket?.defaultPersons;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedThresholdValues() {
    return {
      [OD_SETTINGS_KEYS.otb_pickup_threshold]: !this
        .isOtbPickupThresholdValueInvalid
        ? // @ts-expect-error: Incorrect type
          `${this.thresholdValues[OD_SETTINGS_KEYS.otb_pickup_threshold]}%`
        : null,
      [OD_SETTINGS_KEYS.market_otb_pickup_threshold]: !this
        .isMarketOtbPickupThresholdValueInvalid
        ? // @ts-expect-error: Incorrect type
          `${this.thresholdValues[OD_SETTINGS_KEYS.market_otb_pickup_threshold]}%`
        : null,
      [OD_SETTINGS_KEYS.adr_pickup_threshold]: !this
        .isAdrPickupThresholdValueInvalid
        ? this.hotelAndMarketService.formatRate(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.thresholdValues[OD_SETTINGS_KEYS.adr_pickup_threshold],
            true,
            false,
            { maximumFractionDigits: 0, minimumFractionDigits: 0 },
            false,
            true,
          )
        : null,
      [OD_SETTINGS_KEYS.compset_change_threshold]: !this
        .isCompsetChangeThresholdValueInvalid
        ? this.hotelAndMarketService.formatRate(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.thresholdValues[OD_SETTINGS_KEYS.compset_change_threshold],
            true,
            false,
            { maximumFractionDigits: 0, minimumFractionDigits: 0 },
            false,
          )
        : null,
      [OD_SETTINGS_KEYS.short_term_rental_otb_pickup_threshold]: !this
        .isOTBShortTermRentalThresholdValueInvalid
        ? // @ts-expect-error: Incorrect type
          `${this.thresholdValues[OD_SETTINGS_KEYS.short_term_rental_otb_pickup_threshold]}%`
        : null,
      [OD_SETTINGS_KEYS.short_term_rental_rate_pickup_threshold]: !this
        .isADRShortTermRentalThresholdValueInvalid
        ? this.hotelAndMarketService.formatRate(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.thresholdValues[
              OD_SETTINGS_KEYS.short_term_rental_rate_pickup_threshold
            ],
            true,
            false,
            { maximumFractionDigits: 0, minimumFractionDigits: 0 },
            false,
          )
        : null,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get inputMarketOtbPickupThresholdValueInApiFormat() {
    return (
      // @ts-expect-error: Incorrect type
      this.thresholdValues[OD_SETTINGS_KEYS.market_otb_pickup_threshold] / 100
    );
  }

  // Percentage thresholds need to be divided by 100 again before they can be used for saving
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get inputOtbPickupThresholdValueInApiFormat() {
    // @ts-expect-error: Incorrect type
    return this.thresholdValues[OD_SETTINGS_KEYS.otb_pickup_threshold] / 100;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get inputOTBShortTermRentalThresholdValueInApiFormat() {
    return (
      // @ts-expect-error: Incorrect type
      this.thresholdValues[
        OD_SETTINGS_KEYS.short_term_rental_otb_pickup_threshold
      ] / 100
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isAdrPickupThresholdDisabled() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // @ts-expect-error: Incorrect type
      !this.alertsOptInState[OD_SETTINGS_KEYS.adr_pickup_opt_in] ||
      // @ts-expect-error: Incorrect type
      this.isAlertOptInCheckboxDisabled[DATA_FETCH_ERRORS.revenue]
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isAdrPickupThresholdValueInvalid() {
    return HotelOverviewDashboardModalsAlertsSettings.isInvalidChangeValue(
      // @ts-expect-error: Incorrect type
      this.thresholdValues[OD_SETTINGS_KEYS.adr_pickup_threshold],
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isADRShortTermRentalThresholdDisabled() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // @ts-expect-error: Incorrect type
      !this.alertsOptInState[
        OD_SETTINGS_KEYS.short_term_rental_rate_pickup_opt_in
      ] ||
      // @ts-expect-error: Incorrect type
      this.isAlertOptInCheckboxDisabled[DATA_FETCH_ERRORS.short_term_rental]
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isADRShortTermRentalThresholdValueInvalid() {
    return HotelOverviewDashboardModalsAlertsSettings.isInvalidChangeValue(
      // @ts-expect-error: Incorrect type
      this.thresholdValues[
        OD_SETTINGS_KEYS.short_term_rental_rate_pickup_threshold
      ],
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isAlertOptInCheckboxDisabled() {
    return Object.keys(DATA_FETCH_ERRORS).reduce((isCheckboxDisabled, key) => {
      // @ts-expect-error: Incorrect type
      isCheckboxDisabled[key] = this.alertsDataFetchErrors.includes(key);
      return isCheckboxDisabled;
    }, {});
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isAlertsDataRequestLoading() {
    return (
      this.alertsDataRequestTask.isRunning ||
      this.requestAndSetAlertsDataTask.isRunning
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCompsetChangeThresholdDisabled() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // @ts-expect-error: Incorrect type
      !this.alertsOptInState[OD_SETTINGS_KEYS.compset_change_opt_in] ||
      // @ts-expect-error: Incorrect type
      this.isAlertOptInCheckboxDisabled[DATA_FETCH_ERRORS.compset_pricing]
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCompsetChangeThresholdValueInvalid() {
    return HotelOverviewDashboardModalsAlertsSettings.isInvalidChangeValue(
      // @ts-expect-error: Incorrect type
      this.thresholdValues[OD_SETTINGS_KEYS.compset_change_threshold],
    );
  }

  // Disable the confirm/save button in case the user has entered an invalid value in one of the inputs
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isInputThresholdValueInvalid() {
    return (
      this.isOtbPickupThresholdValueInvalid ||
      this.isMarketOtbPickupThresholdValueInvalid ||
      this.isAdrPickupThresholdValueInvalid ||
      this.isCompsetChangeThresholdValueInvalid ||
      this.isOTBShortTermRentalThresholdValueInvalid ||
      this.isADRShortTermRentalThresholdValueInvalid
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMarketOtbPickupThresholdDisabled() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // @ts-expect-error: Incorrect type
      !this.alertsOptInState[OD_SETTINGS_KEYS.market_otb_pickup_opt_in] ||
      // @ts-expect-error: Incorrect type
      this.isAlertOptInCheckboxDisabled[DATA_FETCH_ERRORS.occupancies]
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMarketOtbPickupThresholdValueInvalid() {
    return HotelOverviewDashboardModalsAlertsSettings.isInvalidPercentageChangeValue(
      // @ts-expect-error: Incorrect type
      this.thresholdValues[OD_SETTINGS_KEYS.market_otb_pickup_threshold],
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isOtbPickupThresholdDisabled() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // @ts-expect-error: Incorrect type
      !this.alertsOptInState[OD_SETTINGS_KEYS.otb_pickup_opt_in] ||
      // @ts-expect-error: Incorrect type
      this.isAlertOptInCheckboxDisabled[DATA_FETCH_ERRORS.occupancies]
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isOtbPickupThresholdValueInvalid() {
    return HotelOverviewDashboardModalsAlertsSettings.isInvalidPercentageChangeValue(
      // @ts-expect-error: Incorrect type
      this.thresholdValues[OD_SETTINGS_KEYS.otb_pickup_threshold],
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isOTBShortTermRentalThresholdDisabled() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // @ts-expect-error: Incorrect type
      !this.alertsOptInState[
        OD_SETTINGS_KEYS.short_term_rental_otb_pickup_opt_in
      ] ||
      // @ts-expect-error: Incorrect type
      this.isAlertOptInCheckboxDisabled[DATA_FETCH_ERRORS.short_term_rental]
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isOTBShortTermRentalThresholdValueInvalid() {
    return HotelOverviewDashboardModalsAlertsSettings.isInvalidChangeValue(
      // @ts-expect-error: Incorrect type
      this.thresholdValues[
        OD_SETTINGS_KEYS.short_term_rental_otb_pickup_threshold
      ],
    );
  }

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    // Store the current overviewSettings values using JSON.stringify (possible for now since the settings aren't super complex. Should allow for easy compare)
    // @ts-expect-error: Incorrect type
    this.overviewSettingsOnLoad = JSON.stringify(
      // @ts-expect-error: Incorrect type
      this.hotelAndMarketService.hotel.overviewSettings,
    );

    // Store the state of all settings opt-in flags here.
    // Note: maybe overkill for now to use an ES6 backing class here?
    // @ts-expect-error: Incorrect type
    this.alertsOptInState = {
      [OD_SETTINGS_KEYS.adr_pickup_opt_in]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.adr_pickup_opt_in
        ],
      [OD_SETTINGS_KEYS.average_demand_level_opt_in]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.average_demand_level_opt_in
        ],
      [OD_SETTINGS_KEYS.price_opportunity_opt_in]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.price_opportunity_opt_in
        ],
      [OD_SETTINGS_KEYS.compset_change_opt_in]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.compset_change_opt_in
        ],
      [OD_SETTINGS_KEYS.compset_soldout_opt_in]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.compset_soldout_opt_in
        ],
      [OD_SETTINGS_KEYS.market_otb_pickup_opt_in]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.market_otb_pickup_opt_in
        ],
      [OD_SETTINGS_KEYS.otb_pickup_opt_in]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.otb_pickup_opt_in
        ],
      [OD_SETTINGS_KEYS.price_position_change_opt_in]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.price_position_change_opt_in
        ],
      // @ts-expect-error: Incorrect type
      [OD_SETTINGS_KEYS.price_opportunity_opt_in]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.price_opportunity_opt_in
        ],
      [OD_SETTINGS_KEYS.short_term_rental_otb_pickup_opt_in]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.short_term_rental_otb_pickup_opt_in
        ],
      [OD_SETTINGS_KEYS.short_term_rental_rate_pickup_opt_in]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.short_term_rental_rate_pickup_opt_in
        ],
    };

    // Initialize the input values with the current threshold values of the user
    // Note that for the percentage values (OTB pickup & Market OTB pickup we need to take the BE format into account and multiply by 100
    // @ts-expect-error: Incorrect type
    this.thresholdValues = {
      [OD_SETTINGS_KEYS.otb_pickup_threshold]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.otb_pickup_threshold
        ] * 100,
      [OD_SETTINGS_KEYS.market_otb_pickup_threshold]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.market_otb_pickup_threshold
        ] * 100,
      [OD_SETTINGS_KEYS.adr_pickup_threshold]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.adr_pickup_threshold
        ],
      [OD_SETTINGS_KEYS.compset_change_threshold]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.compset_change_threshold
        ],
      [OD_SETTINGS_KEYS.short_term_rental_otb_pickup_threshold]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.short_term_rental_otb_pickup_threshold
        ] * 100,
      [OD_SETTINGS_KEYS.short_term_rental_rate_pickup_threshold]:
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings[
          OD_SETTINGS_KEYS.short_term_rental_rate_pickup_threshold
        ],
    };
  }

  // Check if the value is empty or if it's a invalid change value
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  static isInvalidChangeValue(value) {
    return value === null || value < 0;
  }

  // Check if the value is empty or if it's an invalid percentage value (not between 0 and 100 in this case)
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  static isInvalidPercentageChangeValue(value) {
    return value === null || value < 0 || value > 100;
  }

  @action
  async applyChanges(): Promise<void> {
    // Get all modal values and update these in the overviewSettings object under the hotel resource
    // Use set to make sure everything is triggered to update once we close the modal
    // @ts-expect-error: Incorrect type
    Object.keys(this.alertsOptInState).forEach((optInKey) => {
      set(
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.hotel.overviewSettings,
        optInKey,
        // @ts-expect-error: Incorrect type
        this.alertsOptInState[optInKey],
      );
    });

    // Note: before sending the percentage threshold values back to BE, we should divide them by 100 again
    set(
      // @ts-expect-error: Incorrect type
      this.hotelAndMarketService.hotel.overviewSettings,
      OD_SETTINGS_KEYS.otb_pickup_threshold,
      this.inputOtbPickupThresholdValueInApiFormat,
    );
    set(
      // @ts-expect-error: Incorrect type
      this.hotelAndMarketService.hotel.overviewSettings,
      OD_SETTINGS_KEYS.market_otb_pickup_threshold,
      this.inputMarketOtbPickupThresholdValueInApiFormat,
    );
    set(
      // @ts-expect-error: Incorrect type
      this.hotelAndMarketService.hotel.overviewSettings,
      OD_SETTINGS_KEYS.adr_pickup_threshold,
      // @ts-expect-error: Incorrect type
      this.thresholdValues[OD_SETTINGS_KEYS.adr_pickup_threshold],
    );
    set(
      // @ts-expect-error: Incorrect type
      this.hotelAndMarketService.hotel.overviewSettings,
      OD_SETTINGS_KEYS.compset_change_threshold,
      // @ts-expect-error: Incorrect type
      this.thresholdValues[OD_SETTINGS_KEYS.compset_change_threshold],
    );
    set(
      // @ts-expect-error: Incorrect type
      this.hotelAndMarketService.hotel.overviewSettings,
      OD_SETTINGS_KEYS.short_term_rental_otb_pickup_threshold,
      this.inputOTBShortTermRentalThresholdValueInApiFormat,
    );
    set(
      // @ts-expect-error: Incorrect type
      this.hotelAndMarketService.hotel.overviewSettings,
      OD_SETTINGS_KEYS.short_term_rental_rate_pickup_threshold,
      // @ts-expect-error: Incorrect type
      this.thresholdValues[
        OD_SETTINGS_KEYS.short_term_rental_rate_pickup_threshold
      ],
    );

    // Check if a setting has changed or not
    const haveSettingsBeenChanged =
      // @ts-expect-error: Incorrect type
      JSON.stringify(this.hotelAndMarketService.hotel.overviewSettings) !==
      this.overviewSettingsOnLoad;

    if (haveSettingsBeenChanged) {
      // Do the actual save
      try {
        // @ts-expect-error: Incorrect type
        await this.hotelAndMarketService.hotel.save();

        // Close the modal, and also trigger a new Alerts request via the overview settings controller (since the settings have been updated)
        // @ts-expect-error: Incorrect type
        this.args.closeModal(true);

        this.toast.success(this.intl.t('general.changes_saved.body'), {
          title: this.intl.t('general.changes_saved'),
          timeout: TOAST_TIMEOUT_LONG,
          showIcon: true,
        });
      } catch (error) {
        this.errorTracking.captureException(error);

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert().then(() => {
          window.location.reload();
        });
      }
    } else {
      // No settings changed. Close the modal without alert refresh
      // @ts-expect-error: Incorrect type
      this.args.closeModal(false);
    }
  }

  @action
  onCancelClicked(): void {
    // Close the settings modal without relaunching the alerts request (since no settings have changed)
    // @ts-expect-error: Incorrect type
    this.args.closeModal(false);
  }

  @action
  onInsert(): void {
    // Request the alerts data when the modal is opened. API will use the current threshold values if we don't pass any overrides.
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.requestAndSetAlertsDataTask.perform();
  }

  @action
  // @ts-expect-error: Incorrect type
  toggleAlertOptIn(key): void {
    const currentOptInValues = this.alertsOptInState;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    currentOptInValues[key] = !this.alertsOptInState[key];

    // Trigger tracked alertsOptInState
    this.alertsOptInState = currentOptInValues;
  }

  @action
  // @ts-expect-error: Incorrect type
  updateThresholdValue(thresholdKey, event): void {
    const currentThresholdValues = this.thresholdValues;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const filledInValue = event.target.value;
    if (filledInValue === '') {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      currentThresholdValues[thresholdKey] = null;
    } else {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      currentThresholdValues[thresholdKey] = parseFloat(filledInValue, 10);
    }

    // Trigger tracked thresholdValues
    this.thresholdValues = currentThresholdValues;

    // Re-do alerts request with overridden threshold values (only in case all input values are correct)
    if (!this.isInputThresholdValueInvalid) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.requestAndSetAlertsDataTask.perform(
        this.inputOtbPickupThresholdValueInApiFormat,
        this.inputMarketOtbPickupThresholdValueInApiFormat,
        // @ts-expect-error: Incorrect type
        this.thresholdValues[OD_SETTINGS_KEYS.adr_pickup_threshold],
        // @ts-expect-error: Incorrect type
        this.thresholdValues[OD_SETTINGS_KEYS.compset_change_threshold],
        this.inputOTBShortTermRentalThresholdValueInApiFormat,
        // @ts-expect-error: Incorrect type
        this.thresholdValues[
          OD_SETTINGS_KEYS.short_term_rental_rate_pickup_threshold
        ],
      );
    }
  }

  <template>
    {{! @glint-nocheck }}
    <PrismPerfectScrollbar
      class="ic-product-tour-hotel-overview-settings-modal od-alerts-settings-modal-scrollable-container"
      {{didInsert this.onInsert}}
    >
      <div class="od-alerts-settings-modal modal--medium modal--padded">
        <p class="paragraph-300--regular mb-400">
          {{t "overview_dashboard.settings.modal.paragraph.change_info"}}
        </p>

        {{#if
          (or
            this.myotbSs.isOtbSelfServiceUpsellBannerVisible
            this.odAccess.hasAtLeastOneConfigurableTopInsightsTile
          )
        }}
          <section class="main-settings-block">
            <h3>
              {{t "overview_dashboard.header.insights"}}
            </h3>

            {{#if
              (or
                (isOdTileVisible OD_SELECTED_TILES_IDS.my_otb)
                (isOdTileVisible OD_SELECTED_TILES_IDS.market_otb)
              )
            }}
              <PrismCard class="prism-card--alert-settings">
                <:header as |Header|>
                  <Header.title>
                    {{t "overview_dashboard.settings.modal.kpi.pick_up.title"}}
                  </Header.title>
                </:header>
                <:content>
                  {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.my_otb)}}
                    <section class="single-setting-block">
                      <div class="flex items-center">
                        <HotelOverviewDashboardSettingsAlertSettingOptInCheckbox
                          @checkboxLabel={{t
                            "overview_dashboard.settings.modal.kpi.demand_alerts.label.pick_up"
                          }}
                          @checkboxValue={{get
                            this.alertsOptInState
                            OD_SETTINGS_KEYS.otb_pickup_opt_in
                          }}
                          @hasContentAfterCheckbox={{true}}
                          @isDisabled={{get
                            this.isAlertOptInCheckboxDisabled
                            DATA_FETCH_ERRORS.occupancies
                          }}
                          @toggleAlertOptIn={{this.toggleAlertOptIn}}
                          @valueKey={{OD_SETTINGS_KEYS.otb_pickup_opt_in}}
                        />
                      </div>
                      <div class="mt-200 flex flex-row">
                        <div
                          class="od-alerts-settings-modal__input mr-200 flex flex-row"
                        >
                          <PrismInput
                            @autofocus={{false}}
                            @disabled={{this.isOtbPickupThresholdDisabled}}
                            @errorMessage={{t
                              "overview_dashboard.settings.modal.input.error_message.percentage"
                            }}
                            @hasErrors={{this.isOtbPickupThresholdValueInvalid}}
                            @inlineLabel="%"
                            @isTouched={{true}}
                            @onInput={{fn
                              this.updateThresholdValue
                              OD_SETTINGS_KEYS.otb_pickup_threshold
                            }}
                            @removePadding={{true}}
                            @type="number"
                            @value={{get
                              this.thresholdValues
                              OD_SETTINGS_KEYS.otb_pickup_threshold
                            }}
                          />
                        </div>

                        {{#unless this.isOtbPickupThresholdDisabled}}
                          <HotelOverviewDashboardSettingsAlertDaysIndicator
                            @count={{get
                              this.alertsDayIndicatorsData
                              OD_ALERTS_KEYS.otb_pickup
                            }}
                            @isLoading={{this.isAlertsDataRequestLoading}}
                            @thresholdValue={{get
                              this.formattedThresholdValues
                              OD_SETTINGS_KEYS.otb_pickup_threshold
                            }}
                            @translationKey="overview_dashboard.settings.alert.indicator.days_changed_by_minimum"
                          />
                        {{/unless}}
                      </div>
                    </section>
                  {{/if}}

                  {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.market_otb)}}
                    <section class="single-setting-block">
                      <div class="flex items-center">
                        <HotelOverviewDashboardSettingsAlertSettingOptInCheckbox
                          @checkboxLabel={{t
                            "overview_dashboard.settings.modal.kpi.demand_alerts.label.market_pick_up"
                          }}
                          @checkboxValue={{get
                            this.alertsOptInState
                            OD_SETTINGS_KEYS.market_otb_pickup_opt_in
                          }}
                          @hasContentAfterCheckbox={{true}}
                          @isDisabled={{get
                            this.isAlertOptInCheckboxDisabled
                            DATA_FETCH_ERRORS.occupancies
                          }}
                          @toggleAlertOptIn={{this.toggleAlertOptIn}}
                          @valueKey={{OD_SETTINGS_KEYS.market_otb_pickup_opt_in}}
                        />
                      </div>
                      <div class="mt-200 flex flex-row">
                        <div
                          class="od-alerts-settings-modal__input mr-200 flex flex-row"
                        >
                          <PrismInput
                            @autofocus={{false}}
                            @disabled={{this.isMarketOtbPickupThresholdDisabled}}
                            @errorMessage={{t
                              "overview_dashboard.settings.modal.input.error_message.percentage"
                            }}
                            @hasErrors={{this.isMarketOtbPickupThresholdValueInvalid}}
                            @inlineLabel="%"
                            @isTouched={{true}}
                            @onInput={{fn
                              this.updateThresholdValue
                              OD_SETTINGS_KEYS.market_otb_pickup_threshold
                            }}
                            @removePadding={{true}}
                            @type="number"
                            @value={{get
                              this.thresholdValues
                              OD_SETTINGS_KEYS.market_otb_pickup_threshold
                            }}
                          />
                        </div>

                        {{#unless this.isMarketOtbPickupThresholdDisabled}}
                          <HotelOverviewDashboardSettingsAlertDaysIndicator
                            @count={{get
                              this.alertsDayIndicatorsData
                              OD_ALERTS_KEYS.market_otb_pickup
                            }}
                            @isLoading={{this.isAlertsDataRequestLoading}}
                            @thresholdValue={{get
                              this.formattedThresholdValues
                              OD_SETTINGS_KEYS.market_otb_pickup_threshold
                            }}
                            @translationKey="overview_dashboard.settings.alert.indicator.days_changed_by_minimum"
                          />
                        {{/unless}}
                      </div>
                    </section>
                  {{/if}}
                </:content>
              </PrismCard>
            {{else if this.myotbSs.isOtbSelfServiceUpsellBannerVisible}}
              {{! We reuse the prism-card alerts settings styling for the OTB SS upsell banner here }}
              <div class="prism-card--alert-settings">
                <GeneralEmptyStateOtbBanner
                  @otbSelfServiceEntryPoint={{OTB_SS_ENTRY_POINTS.OD_SETTINGS}}
                  @otbSelfServiceEntryProduct={{OTB_SS_VIA_QP_VALUES.OVERVIEW_DASHBOARD}}
                />
              </div>
            {{/if}}

            {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.my_adr)}}
              <PrismCard class="prism-card--alert-settings">
                <:header as |Header|>
                  <Header.title>
                    {{t
                      "overview_dashboard.settings.modal.kpi.adr_alerts.title"
                    }}
                  </Header.title>
                </:header>
                <:content>
                  <section class="single-setting-block">
                    <div class="flex items-center">
                      <HotelOverviewDashboardSettingsAlertSettingOptInCheckbox
                        @checkboxLabel={{t
                          "overview_dashboard.settings.modal.kpi.adr_alerts.label.change"
                        }}
                        @checkboxValue={{get
                          this.alertsOptInState
                          OD_SETTINGS_KEYS.adr_pickup_opt_in
                        }}
                        @hasContentAfterCheckbox={{true}}
                        @isDisabled={{get
                          this.isAlertOptInCheckboxDisabled
                          DATA_FETCH_ERRORS.revenue
                        }}
                        @toggleAlertOptIn={{this.toggleAlertOptIn}}
                        @valueKey={{OD_SETTINGS_KEYS.adr_pickup_opt_in}}
                      />
                    </div>
                    <div class="mt-200 flex flex-row">
                      <div
                        class="od-alerts-settings-modal__input mr-200 flex flex-row"
                      >
                        <PrismInput
                          @autofocus={{false}}
                          @disabled={{this.isAdrPickupThresholdDisabled}}
                          @errorMessage={{t
                            "overview_dashboard.settings.modal.input.error_message.change_value"
                          }}
                          @haserrors={{this.isAdrPickupThresholdValueInvalid}}
                          @inlineLabel={{this.ADRCurrencySymbol}}
                          @isTouched={{true}}
                          @onInput={{fn
                            this.updateThresholdValue
                            OD_SETTINGS_KEYS.adr_pickup_threshold
                          }}
                          @removePadding={{true}}
                          @type="number"
                          @value={{get
                            this.thresholdValues
                            OD_SETTINGS_KEYS.adr_pickup_threshold
                          }}
                        />
                      </div>

                      {{#unless this.isAdrPickupThresholdDisabled}}
                        <HotelOverviewDashboardSettingsAlertDaysIndicator
                          @count={{get
                            this.alertsDayIndicatorsData
                            OD_ALERTS_KEYS.adr_pickup
                          }}
                          @isLoading={{this.isAlertsDataRequestLoading}}
                          @thresholdValue={{get
                            this.formattedThresholdValues
                            OD_SETTINGS_KEYS.adr_pickup_threshold
                          }}
                          @translationKey="overview_dashboard.settings.alert.indicator.days_changed_by_minimum"
                        />
                      {{/unless}}
                    </div>
                  </section>
                </:content>
              </PrismCard>
            {{/if}}

          </section>
        {{/if}}

        {{#if this.odAccess.hasAtLeastOneConfigurableMarketTrendsTile}}
          <section class="main-settings-block">
            <h3>
              {{t "overview_dashboard.header.market_trends"}}
            </h3>

            {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.pricing_difference)}}
              <PrismCard class="prism-card--alert-settings">
                <:header as |Header|>
                  <Header.title>
                    {{t
                      "overview_dashboard.settings.modal.kpi.pricing_alerts.title"
                    }}
                  </Header.title>
                </:header>
                <:content>
                  <section class="single-setting-block">
                    <div class="flex items-center">
                      <HotelOverviewDashboardSettingsAlertSettingOptInCheckbox
                        @checkboxLabel={{t
                          "overview_dashboard.settings.modal.kpi.demand_alerts.label.market_trends.competitor_change"
                        }}
                        @checkboxValue={{get
                          this.alertsOptInState
                          OD_SETTINGS_KEYS.compset_change_opt_in
                        }}
                        @hasContentAfterCheckbox={{true}}
                        @isDisabled={{get
                          this.isAlertOptInCheckboxDisabled
                          DATA_FETCH_ERRORS.compset_pricing
                        }}
                        @toggleAlertOptIn={{this.toggleAlertOptIn}}
                        @valueKey={{OD_SETTINGS_KEYS.compset_change_opt_in}}
                      />
                    </div>
                    <div class="mt-200 flex flex-row">
                      <div
                        class="od-alerts-settings-modal__input mr-200 flex flex-row"
                      >
                        <PrismInput
                          @autofocus={{false}}
                          @disabled={{this.isCompsetChangeThresholdDisabled}}
                          @errorMessage={{t
                            "overview_dashboard.settings.modal.input.error_message.change_value"
                          }}
                          @hasErrors={{this.isCompsetChangeThresholdValueInvalid}}
                          @inlineLabel={{this.hotelAndMarketService.hotel.currencySymbol}}
                          @isTouched={{true}}
                          @onInput={{fn
                            this.updateThresholdValue
                            OD_SETTINGS_KEYS.compset_change_threshold
                          }}
                          @removePadding={{true}}
                          @type="number"
                          @value={{get
                            this.thresholdValues
                            OD_SETTINGS_KEYS.compset_change_threshold
                          }}
                        />
                      </div>

                      {{#unless this.isCompsetChangeThresholdDisabled}}
                        <HotelOverviewDashboardSettingsAlertDaysIndicator
                          @count={{get
                            this.alertsDayIndicatorsData
                            OD_ALERTS_KEYS.compset_change
                          }}
                          @isLoading={{this.isAlertsDataRequestLoading}}
                          @thresholdValue={{get
                            this.formattedThresholdValues
                            OD_SETTINGS_KEYS.compset_change_threshold
                          }}
                          @translationKey="overview_dashboard.settings.alert.indicator.competitors_changed_by_minimum"
                        />
                      {{/unless}}
                    </div>
                  </section>

                  <section class="single-setting-block">
                    <div class="flex items-center">
                      <HotelOverviewDashboardSettingsAlertSettingOptInCheckbox
                        @checkboxLabel={{t
                          "overview_dashboard.settings.modal.kpi.demand_alerts.label.competitors"
                        }}
                        @checkboxValue={{get
                          this.alertsOptInState
                          OD_SETTINGS_KEYS.compset_soldout_opt_in
                        }}
                        @isDisabled={{get
                          this.isAlertOptInCheckboxDisabled
                          DATA_FETCH_ERRORS.compset_pricing
                        }}
                        @toggleAlertOptIn={{this.toggleAlertOptIn}}
                        @valueKey={{OD_SETTINGS_KEYS.compset_soldout_opt_in}}
                      />
                    </div>
                    <div class="single-setting-block__subtitle mt-100 ml-500">
                      {{t
                        "overview_dashboard.settings.modal.kpi.demand_alerts.label.competitors.subtitle"
                      }}
                    </div>
                  </section>

                  <section class="single-setting-block">
                    <div class="flex items-center">
                      <HotelOverviewDashboardSettingsAlertSettingOptInCheckbox
                        @checkboxLabel={{t
                          "overview_dashboard.settings.modal.kpi.demand_alerts.label.price_position"
                        }}
                        @checkboxValue={{get
                          this.alertsOptInState
                          OD_SETTINGS_KEYS.price_position_change_opt_in
                        }}
                        @isDisabled={{get
                          this.isAlertOptInCheckboxDisabled
                          DATA_FETCH_ERRORS.priceposition
                        }}
                        @toggleAlertOptIn={{this.toggleAlertOptIn}}
                        @valueKey={{OD_SETTINGS_KEYS.price_position_change_opt_in}}
                      />
                    </div>
                    <div class="single-setting-block__subtitle mt-100 ml-500">
                      {{t
                        "overview_dashboard.settings.modal.kpi.demand_alerts.label.price_position.subtitle"
                      }}
                    </div>
                  </section>

                  {{#if this.odAccess.isMarketInsightFull}}
                    <section class="single-setting-block">
                      <div class="flex items-center">
                        <HotelOverviewDashboardSettingsAlertSettingOptInCheckbox
                          @checkboxLabel={{t
                            "overview_dashboard.settings.modal.kpi.demand_alerts.label.price_opportunity"
                          }}
                          @checkboxValue={{get
                            this.alertsOptInState
                            OD_SETTINGS_KEYS.price_opportunity_opt_in
                          }}
                          @isDisabled={{get
                            this.isAlertOptInCheckboxDisabled
                            DATA_FETCH_ERRORS.demand
                          }}
                          @toggleAlertOptIn={{this.toggleAlertOptIn}}
                          @valueKey={{OD_SETTINGS_KEYS.price_opportunity_opt_in}}
                          {{! price_opportunity comes from the Market Insight API }}
                          {{! therefore, isDisabled looks at the demand meta error }}
                        />
                      </div>
                      <div class="single-setting-block__subtitle mt-100 ml-500">
                        {{t
                          "overview_dashboard.settings.modal.kpi.demand_alerts.label.price_opportunity.subtitle"
                        }}
                      </div>
                    </section>
                  {{/if}}
                </:content>
              </PrismCard>
            {{/if}}

            {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.demand_level)}}
              <PrismCard class="prism-card--alert-settings">
                <:header as |Header|>
                  <Header.title>
                    {{t
                      "overview_dashboard.settings.modal.kpi.demand_alerts.title"
                    }}
                  </Header.title>
                </:header>
                <:content>
                  <section class="single-setting-block">
                    <div class="flex items-center">
                      <HotelOverviewDashboardSettingsAlertSettingOptInCheckbox
                        @checkboxLabel={{t
                          "overview_dashboard.settings.modal.kpi.demand_alerts.label.change"
                        }}
                        @checkboxValue={{get
                          this.alertsOptInState
                          OD_SETTINGS_KEYS.average_demand_level_opt_in
                        }}
                        @isDisabled={{get
                          this.isAlertOptInCheckboxDisabled
                          DATA_FETCH_ERRORS.demand
                        }}
                        @toggleAlertOptIn={{this.toggleAlertOptIn}}
                        @valueKey={{OD_SETTINGS_KEYS.average_demand_level_opt_in}}
                      />
                    </div>
                    <div class="single-setting-block__subtitle mt-100 ml-500">
                      {{t
                        "overview_dashboard.settings.modal.kpi.demand_alerts.label.change.subtitle"
                      }}
                    </div>
                  </section>
                </:content>
              </PrismCard>
            {{/if}}

            {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.short_term_rental)}}
              <PrismCard class="prism-card--alert-settings">
                <:header as |Header|>
                  <div class="flex items-center justify-start gap-200">
                    <Header.title>{{t
                        "general.short_term_rental"
                      }}</Header.title>
                    <PrismIcon @name="short-term-rental" />
                  </div>
                  <p class="prism-card__subtitle mt-100">
                    {{t
                      "overview_dashbpard.settings.modal.kpi.short_term_rental.subtitle"
                    }}
                  </p>
                </:header>
                <:content>
                  <section class="single-setting-block">
                    <div class="flex items-center">
                      <HotelOverviewDashboardSettingsAlertSettingOptInCheckbox
                        @checkboxLabel={{t
                          "overview_dashboard.settings.modal.kpi.short_term_rental.label.otb_changed"
                        }}
                        @checkboxValue={{get
                          this.alertsOptInState
                          OD_SETTINGS_KEYS.short_term_rental_otb_pickup_opt_in
                        }}
                        @hasContentAfterCheckbox={{true}}
                        @isDisabled={{get
                          this.isAlertOptInCheckboxDisabled
                          DATA_FETCH_ERRORS.short_term_rental
                        }}
                        @toggleAlertOptIn={{this.toggleAlertOptIn}}
                        @valueKey={{OD_SETTINGS_KEYS.short_term_rental_otb_pickup_opt_in}}
                      />
                    </div>
                    <div class="mt-200 flex flex-row">
                      <div
                        class="od-alerts-settings-modal__input mr-200 flex flex-row"
                      >
                        <PrismInput
                          @autofocus={{false}}
                          @disabled={{this.isOTBShortTermRentalThresholdDisabled}}
                          @errorMessage={{t
                            "overview_dashboard.settings.modal.input.error_message.percentage"
                          }}
                          @hasErrors={{this.isOTBShortTermRentalThresholdValueInvalid}}
                          @inlineLabel="%"
                          @isTouched={{true}}
                          @onInput={{fn
                            this.updateThresholdValue
                            OD_SETTINGS_KEYS.short_term_rental_otb_pickup_threshold
                          }}
                          @removePadding={{true}}
                          @type="number"
                          @value={{get
                            this.thresholdValues
                            OD_SETTINGS_KEYS.short_term_rental_otb_pickup_threshold
                          }}
                        />
                      </div>

                      {{#unless this.isOTBShortTermRentalThresholdDisabled}}
                        <HotelOverviewDashboardSettingsAlertDaysIndicator
                          @count={{get
                            this.alertsDayIndicatorsData
                            OD_ALERTS_KEYS.short_term_rental_otb_pickup
                          }}
                          @isLoading={{this.isAlertsDataRequestLoading}}
                          @thresholdValue={{get
                            this.formattedThresholdValues
                            OD_SETTINGS_KEYS.short_term_rental_otb_pickup_threshold
                          }}
                          @translationKey="overview_dashboard.settings.alert.indicator.days_changed_by_minimum"
                        />
                      {{/unless}}
                    </div>
                  </section>

                  <section class="single-setting-block">
                    <div class="flex items-center">
                      <HotelOverviewDashboardSettingsAlertSettingOptInCheckbox
                        @checkboxLabel={{t
                          "overview_dashboard.settings.modal.kpi.short_term_rental.label.rate_changed"
                        }}
                        @checkboxValue={{get
                          this.alertsOptInState
                          OD_SETTINGS_KEYS.short_term_rental_rate_pickup_opt_in
                        }}
                        @hasContentAfterCheckbox={{true}}
                        @isDisabled={{get
                          this.isAlertOptInCheckboxDisabled
                          DATA_FETCH_ERRORS.short_term_rental
                        }}
                        @toggleAlertOptIn={{this.toggleAlertOptIn}}
                        @valueKey={{OD_SETTINGS_KEYS.short_term_rental_rate_pickup_opt_in}}
                      />
                    </div>
                    <div class="mt-200 flex flex-row">
                      <div
                        class="od-alerts-settings-modal__input mr-200 flex flex-row"
                      >
                        <PrismInput
                          @autofocus={{false}}
                          @disabled={{this.isADRShortTermRentalThresholdDisabled}}
                          @errorMessage={{t
                            "overview_dashboard.settings.modal.input.error_message.change_value"
                          }}
                          @hasErrors={{this.isADRShortTermRentalThresholdValueInvalid}}
                          @inlineLabel={{this.hotelAndMarketService.hotel.currencySymbol}}
                          @isTouched={{true}}
                          @onInput={{fn
                            this.updateThresholdValue
                            OD_SETTINGS_KEYS.short_term_rental_rate_pickup_threshold
                          }}
                          @removePadding={{true}}
                          @type="number"
                          @value={{get
                            this.thresholdValues
                            OD_SETTINGS_KEYS.short_term_rental_rate_pickup_threshold
                          }}
                        />
                      </div>

                      {{#unless this.isADRShortTermRentalThresholdDisabled}}
                        <HotelOverviewDashboardSettingsAlertDaysIndicator
                          @count={{get
                            this.alertsDayIndicatorsData
                            OD_ALERTS_KEYS.short_term_rental_rate_pickup
                          }}
                          @isLoading={{this.isAlertsDataRequestLoading}}
                          @thresholdValue={{get
                            this.formattedThresholdValues
                            OD_SETTINGS_KEYS.short_term_rental_rate_pickup_threshold
                          }}
                          @translationKey="overview_dashboard.settings.alert.indicator.days_changed_by_minimum"
                        />
                      {{/unless}}
                    </div>
                  </section>
                </:content>
              </PrismCard>
            {{/if}}

          </section>
        {{/if}}
      </div>
    </PrismPerfectScrollbar>

    <div class="settings-modal__footer">
      <GeneralFormFieldsCancelAndConfirm
        @cancel={{this.onCancelClicked}}
        @confirm={{this.applyChanges}}
        @isConfirmDisabled={{this.isInputThresholdValueInvalid}}
      />
    </div>
  </template>
}
